import { deviationPercent, drugUnitValue, roundQuantity } from '@/composables/inventory/inventoryTable';
import type { MedicineBulkCountPayload, MedicineBulkCountRow } from '@/types';

/** The count reason that has to be explained in the note ("Andet"). */
export const COUNT_REASON_OTHER_ID = 6;

/** What the user typed into one row; every field is raw input (a number input may hand over a number or a string). */
export type BulkCountEntry = {
    counted: string | number;
    reasonId: number | null;
    note: string;
};

export const emptyEntry = (): BulkCountEntry => ({ counted: '', reasonId: null, note: '' });

/** Whether anything was typed into a row, which a change of farm or closing the dialog would lose. */
export function isBulkCountEntryDirty(entry: BulkCountEntry): boolean {
    return String(entry.counted ?? '').trim() !== '' || entry.reasonId !== null || entry.note.trim() !== '';
}

export type BulkCountRowProblem = 'invalidQuantity' | 'reasonRequired' | 'noteRequired';

export type BulkCountRowState = {
    /** Nothing counted: the row is greyed out and not saved. */
    skipped: boolean;
    counted: number | null;
    difference: number | null;
    percent: number | null;
    problems: BulkCountRowProblem[];
};

/**
 * Where one row stands: skipped when nothing was counted, otherwise the figures it would save and what
 * stops it from being saved. A difference needs a reason, and the reason "Andet" needs a note - the same
 * rules the server applies.
 */
export function evaluateBulkRow(row: MedicineBulkCountRow, entry: BulkCountEntry): BulkCountRowState {
    const raw = String(entry.counted ?? '').trim();

    if (raw === '') {
        return { skipped: true, counted: null, difference: null, percent: null, problems: [] };
    }

    const counted = Number(raw);

    if (!Number.isFinite(counted) || counted < 0) {
        return { skipped: false, counted: null, difference: null, percent: null, problems: ['invalidQuantity'] };
    }

    const difference = roundQuantity(counted - row.calculated);
    const problems: BulkCountRowProblem[] = [];

    if (difference !== 0 && entry.reasonId === null) {
        problems.push('reasonRequired');
    }
    if (entry.reasonId === COUNT_REASON_OTHER_ID && entry.note.trim() === '') {
        problems.push('noteRequired');
    }

    return { skipped: false, counted, difference, percent: deviationPercent(difference, row.calculated), problems };
}

/** The request body for every row that was counted; skipped rows are left out. */
export function bulkCountPayload(farmId: number, date: string, rows: MedicineBulkCountRow[], entries: Record<string, BulkCountEntry>): MedicineBulkCountPayload {
    return {
        farm_id: farmId,
        date,
        rows: rows.flatMap((row) => {
            const entry = entries[drugUnitValue(row)] ?? emptyEntry();
            const { counted } = evaluateBulkRow(row, entry);

            return counted === null ? [] : [{ drug_id: row.drug_id, dosage_unit_id: row.unit_id, counted, reason_id: entry.reasonId, note: entry.note.trim() === '' ? null : entry.note.trim() }];
        }),
    };
}
